/**
 * useAllArticles — single source of truth for "every article on Memory
 * Lane right now". Merges:
 *   - The static `ARTICLES` array from /lib/seoArticles.js (94 curated articles)
 *   - Studio-published articles from /api/articles/list (Mongo-backed)
 *
 * The hook caches the merged list in a module-level singleton so each
 * consumer (Resources page, universal search, author page, related-articles
 * blocks) doesn't refetch. Studio articles automatically appear in every
 * surface that uses this hook within ~1s of being published.
 */
import { useEffect, useState } from "react";
import { ARTICLES as STATIC_ARTICLES } from "@/lib/seoArticles";
import { api } from "@/lib/api";

let _cache = null;
let _inflight = null;

async function _load() {
    if (_cache) return _cache;
    if (_inflight) return _inflight;
    _inflight = (async () => {
        try {
            const r = await api.get("/articles/list");
            const studio = (r.data?.articles || []).map((a) => ({
                ...a,
                // Match the static-article shape so consumers can treat
                // them identically. Studio articles don't have body
                // until ArticlePage fetches them — that's fine here
                // since list surfaces only need title/h1/meta/tags.
                body: a.body || [],
                faq: a.faq || [],
                source: "studio",
            }));
            // Studio first → newest content surfaces at the top of lists.
            _cache = [...studio, ...STATIC_ARTICLES];
        } catch {
            // Static-only fallback if the list endpoint is offline.
            _cache = STATIC_ARTICLES;
        } finally {
            _inflight = null;
        }
        return _cache;
    })();
    return _inflight;
}

export function useAllArticles() {
    const [articles, setArticles] = useState(_cache || STATIC_ARTICLES);
    useEffect(() => {
        let cancelled = false;
        _load().then((all) => {
            if (!cancelled) setArticles(all);
        });
        return () => { cancelled = true; };
    }, []);
    return articles;
}

/**
 * Invalidate the in-memory cache. Called after the Content Studio
 * publishes a new article so the next consumer fetch picks it up.
 */
export function invalidateArticleCache() {
    _cache = null;
}
